/**
 * Identity field policy — framework-driven visibility + write-gating.
 *
 * Tenants activate compliance frameworks (ITAR / CMMC / HIPAA / GDPR / EU AI Act)
 * through the Trust → Framework Activation Wizard. Each framework dictates which
 * regulatory identity fields are visible in the Users module and writable via
 * PATCH /users/:id/identity-fields.
 *
 * The resolver is the single source of truth — used by:
 *   - Web: useIdentityFieldPolicy() hook drives Users-tab dropdown filter, Create
 *     User dialog Clearance step (skipped entirely when no fields are visible),
 *     and User-detail Security panel.
 *   - API: PATCH /users/:id/identity-fields rejects keys outside the visible
 *     set with 400 FRAMEWORK_NOT_ACTIVE so a tenant can't smuggle ITAR clearance
 *     into a non-ITAR org.
 *
 * Deactivation policy: when a framework is deactivated, the resolver hides the
 * UI + blocks future writes, but **never nulls existing data**. Re-activation
 * exposes the same values — compliance officers never lose history.
 */

import { z } from 'zod';

// ─── Frameworks ──────────────────────────────────────────────────────────────

/**
 * The five v1 compliance frameworks. Mirrors `ComplianceFrameworkEnum` in
 * packages/api/src/routes/v1/governance-phase4-schemas.ts. Tenants store the
 * active set as a JSONB array on `governance_configs.compliance_frameworks`.
 */
export const ComplianceFrameworkSchema = z.enum([
  'itar',
  'cmmc',
  'hipaa',
  'gdpr',
  'eu_ai_act',
]);
export type ComplianceFramework = z.infer<typeof ComplianceFrameworkSchema>;

// ─── Identity field keys ─────────────────────────────────────────────────────

/**
 * The set of regulatory identity fields the policy can gate. Keys that don't
 * appear here are NOT framework-conditional (e.g., `email`, `firstName` are
 * always visible).
 */
export const IdentityFieldKeySchema = z.enum([
  'clearance',
  'usPersonVerified',
  'usPersonVerifiedAt',
  'usPersonReverifyDue',
  'classificationAccess',
  'exportControlAccess',
  'hipaaBaaStatus',
  'hipaaBaaSignedAt',
  'hipaaBaaExpiresAt',
]);
export type IdentityFieldKey = z.infer<typeof IdentityFieldKeySchema>;

// ─── Resolver output ─────────────────────────────────────────────────────────

export interface IdentityFieldPolicy {
  /** Fields the UI should render and the API should accept on PATCH. */
  visible: ReadonlySet<IdentityFieldKey>;
  /**
   * Fields that MUST be set when the user record reaches "active" status.
   * The Create User wizard hard-fails on save if any required field is empty.
   * The PATCH endpoint accepts partial updates regardless.
   */
  required: ReadonlySet<IdentityFieldKey>;
  /**
   * Fields that, when invisible, cause PATCH to return 400 FRAMEWORK_NOT_ACTIVE.
   * Always equals the inverse of `visible` for keys defined in
   * `IdentityFieldKeySchema` — exposed as its own set to make the API layer
   * self-documenting.
   */
  forbidden: ReadonlySet<IdentityFieldKey>;
}

const ALL_KEYS: IdentityFieldKey[] = IdentityFieldKeySchema.options;

// ─── Per-framework field grants ──────────────────────────────────────────────

const ITAR_VISIBLE: IdentityFieldKey[] = [
  'clearance',
  'usPersonVerified',
  'usPersonVerifiedAt',
  'usPersonReverifyDue',
  'classificationAccess',
  'exportControlAccess',
];

const ITAR_REQUIRED: IdentityFieldKey[] = [
  // Clearance defaults to 'unclassified' so it's effectively always set, but we
  // require the field to exist in the form. The 22 CFR 120.15 coupling rules
  // (clearance ≥ secret ⇒ usPersonVerified=true with timestamp) are enforced
  // by the existing F61 Zod refinement on the PATCH route, not here.
  'exportControlAccess',
];

const CMMC_VISIBLE: IdentityFieldKey[] = [
  // CMMC L2 mandates CUI handling tracking. The clearance dropdown is reused
  // (CUI is a level on the same scale) but US-Person verification is optional
  // unless the tenant has *also* activated ITAR.
  'clearance',
  'classificationAccess',
];

const HIPAA_VISIBLE: IdentityFieldKey[] = [
  'hipaaBaaStatus',
  'hipaaBaaSignedAt',
  'hipaaBaaExpiresAt',
];

const HIPAA_REQUIRED: IdentityFieldKey[] = [
  // BAA status is required because the default 'not_applicable' is itself a
  // valid answer for direct-employee workforce members. Choosing it is an
  // affirmative compliance decision, so the field must appear on the form.
  'hipaaBaaStatus',
];

// GDPR + EU AI Act intentionally have no user-record field grants in v1.
// They affect data residency / audit retention / operator-role logging, which
// live elsewhere (governance_configs, audit_logs, agent_versions).

// ─── Resolver ────────────────────────────────────────────────────────────────

/**
 * Compute the identity field policy for a tenant given its active frameworks.
 *
 * Pure function — no DB calls, no caching. Callers are responsible for fetching
 * `governance_configs.complianceFrameworks` and passing it in. This makes the
 * resolver trivially testable + safe to call in render paths.
 */
export function resolveIdentityFieldPolicy(
  activeFrameworks: readonly ComplianceFramework[]
): IdentityFieldPolicy {
  const visible = new Set<IdentityFieldKey>();
  const required = new Set<IdentityFieldKey>();

  const has = (fw: ComplianceFramework) => activeFrameworks.includes(fw);

  if (has('itar')) {
    ITAR_VISIBLE.forEach((k) => visible.add(k));
    ITAR_REQUIRED.forEach((k) => required.add(k));
  }
  if (has('cmmc')) {
    CMMC_VISIBLE.forEach((k) => visible.add(k));
  }
  if (has('hipaa')) {
    HIPAA_VISIBLE.forEach((k) => visible.add(k));
    HIPAA_REQUIRED.forEach((k) => required.add(k));
  }
  // gdpr + eu_ai_act: no user-record fields in v1.

  const forbidden = new Set<IdentityFieldKey>(
    ALL_KEYS.filter((k) => !visible.has(k))
  );

  return { visible, required, forbidden };
}

/**
 * Convenience: does the tenant's active framework set surface ANY identity
 * fields at all? Drives the "skip the Clearance step entirely" branch in the
 * Create User wizard.
 */
export function hasAnyIdentityFields(
  activeFrameworks: readonly ComplianceFramework[]
): boolean {
  return resolveIdentityFieldPolicy(activeFrameworks).visible.size > 0;
}

// ─── Document / folder classification controls (Library LB6-T7) ─────────────

/**
 * The two classification CONTROLS a Library document or folder carries.
 * Decision 28 (B7): they are hidden until a compliance framework is active,
 * by the same rule as people's clearance fields; while hidden they are
 * neither shown nor recorded (the api refuses them with 400
 * FRAMEWORK_NOT_ACTIVE). A classification already stored is still enforced by
 * the Document visibility resolver, which never asks this rule.
 */
export const ClassificationControlKeySchema = z.enum(['sensitivity', 'exportControl']);
export type ClassificationControlKey = z.infer<typeof ClassificationControlKeySchema>;

/**
 * OQ-L6-1 (founder 2026-10-22): each control follows the people field it
 * mirrors, per field — SENSITIVITY shows wherever a person's `clearance`
 * shows (ITAR or CMMC); EXPORT CONTROL wherever `exportControlAccess` shows
 * (ITAR only). ONE declaration: the framework → field grants above decide
 * both, so the two rails cannot drift.
 */
const CLASSIFICATION_CONTROL_FOLLOWS: Record<ClassificationControlKey, IdentityFieldKey> = {
  sensitivity: 'clearance',
  exportControl: 'exportControlAccess',
};

/** What GET /knowledge/classification-policy answers (the web hook reads it). */
export const ClassificationControlPolicySchema = z.object({
  visible: z.object({
    sensitivity: z.boolean(),
    exportControl: z.boolean(),
  }),
  activeFrameworks: z.array(ComplianceFrameworkSchema),
});
export type ClassificationControlPolicy = z.infer<typeof ClassificationControlPolicySchema>;

/**
 * Which classification controls a tenant's active frameworks show. Pure; the
 * caller resolves the frameworks (the api through policy/framework-resolver.ts,
 * which throws rather than answer "none" on a read failure).
 */
export function resolveClassificationControlPolicy(
  activeFrameworks: readonly ComplianceFramework[]
): ClassificationControlPolicy {
  const people = resolveIdentityFieldPolicy(activeFrameworks);
  return {
    visible: {
      sensitivity: people.visible.has(CLASSIFICATION_CONTROL_FOLLOWS.sensitivity),
      exportControl: people.visible.has(CLASSIFICATION_CONTROL_FOLLOWS.exportControl),
    },
    activeFrameworks: [...activeFrameworks],
  };
}
